import React, { Suspense } from 'react';

import { preloadedRouteComponent } from './awaitPrerenderedRouteChunk';

export interface PrerenderedLazyRouteProps {
  /** The route's path, as declared on its <Route>. */
  path: string;
  /** The shared route shimmer — used only when nothing was preloaded. */
  fallback: React.ReactNode;
  /** The `React.lazy` element for this route. */
  children: React.ReactNode;
}

/**
 * Renders a prerendered route's component synchronously if it was loaded before
 * React's first render, and falls back to `lazy` + Suspense otherwise.
 *
 * WHY THIS EXISTS. `/privacy` and `/terms` ship their full text in the HTML.
 * `React.lazy` suspends on its first read even when the module is already in
 * webpack's cache, so React's first commit replaced that text with the route
 * shimmer for 2-3 frames before putting it back — measured, on the two pages an
 * external reviewer is most likely to open. Bypassing `lazy` when the module is
 * in hand is what removes the commit that does it.
 *
 * WHAT IT IS NOT. Not an optimisation, and not a change to how anything loads.
 * The chunk is still lazy and still only downloaded by visitors who ask for
 * these routes. The preload in src/index.js only runs when the visitor landed
 * ON one of these routes, so an in-app navigation to /terms takes the Suspense
 * path exactly as before — correctly, because there is no prerendered text on
 * screen to protect in that case.
 */
export function PrerenderedLazyRoute({
  path,
  fallback,
  children,
}: PrerenderedLazyRouteProps): React.ReactElement {
  const Preloaded = preloadedRouteComponent(path);
  if (Preloaded) return <Preloaded />;
  return <Suspense fallback={fallback}>{children}</Suspense>;
}
